import { createStackNavigator } from '@react-navigation/stack';
import { Formik, FormikProps } from 'formik';
import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Platform } from 'react-native';
import { Toast } from 'react-native-toast-notifications';
import { InferType } from 'yup';

import { Alert } from '@components/Alert';
import { CustomToast } from '@components/CustomToast/CustomToast';
import { showSubmitErrorToast } from '@components/CustomToast/showSubmitErrorToast';
import { StatusToast } from '@components/CustomToast/StatusToast.web';
import ColorPicker from '@components/FormModals/ColorPicker';
import MobileIconPicker from '@components/FormModals/IconPicker';
import IconPicker from '@components/FormModals/IconPicker.web';
import { MemberSelect } from '@components/FormModals/MemberSelect';
import { TeamSelect } from '@components/FormModals/TeamSelect';
import SecondaryHeader from '@components/Headers/SecondaryHeader';
import { SubscribeModal } from '@components/MobileOnboarding/SubscribeModal';
import { SuccessModal } from '@components/MobileOnboarding/SuccessModal';
import { WebModal } from '@components/Modals/WebModal.web';
import { ProjectCreateBottomSheet } from '@components/Projects/ProjectCreateBottomSheet';
import ProjectForm from '@components/Projects/ProjectForm';
import {
  getProjectFormFieldErrors,
  shouldShowProjectFormFallbackAlert,
} from '@components/Projects/projectFormFieldErrors';
import { buildProjectMemberPayload } from '@components/Projects/projectFormMemberPayload';
import { useWebDrawer } from '@components/Web/Drawer/WebDrawerContext';
import {
  DocumentAttributes,
  GetMeDocument,
  GetMeQuery,
  LocalFile,
  Project,
  ProjectMemberRole,
  ProjectMemberRoleAttributes,
  useCreateProjectInvitationMutation,
  useCreateProjectMutation,
  useListProductsQuery,
  useUpdateOnboardingStateMutation,
  useUpdateProjectMutation,
} from '@graphql/generated';
import { useInviteMemberNudge } from '@hooks/useInviteMemberNudge';
import { useListTeamsFromQuery } from '@hooks/useListTeamsFromQuery';
import useMe from '@hooks/useMe';
import { useOwnerNudge } from '@hooks/useOwnerNudge';
import { useProjectTutorialWeb } from '@hooks/useProjectTutorialWeb';
import useRevenueCat, { loginRevenueCat } from '@hooks/useRevenueCat';
import { useTeamFromQuery } from '@hooks/useTeamFromQuery';
import { stackScreenOptions } from '@navigation/payments/payments-stack';
import { useAppNavigation } from '@navigation/useAppNavigation';
import { projectFormSchema } from '@schemas/projectFormSchema';
import { ProjectCopySearch } from '@screens/Projects/ProjectCopySearch';
import { ProjectCopySelect } from '@screens/Projects/ProjectCopySelect.web';
import { ProjectLocationSearch } from '@screens/Projects/ProjectLocationSearch';
import { MIXPANEL_EVENTS } from '@src/constants/constants';
import { useMixpanel } from '@src/utils/mixpanel';
import { getSubmitErrorMessage } from '@utils/getSubmitErrorMessage';
import {
  isConnectionFailure,
  mutationFailed,
  toMutationError,
} from '@utils/graphqlError';
import { addOnboardingInvitees } from '@utils/onboardingInvitedMembers';
import { defaultProjectRole, resolveProjectTeamUuid } from '@utils/projects';
import { runSubmitPreflight } from '@utils/runSubmitPreflight';
import {
  hasReachedPersonalProjectLimit,
  shouldShowPaywall,
} from '@utils/teams/paywall';

export type FormValues = InferType<typeof projectFormSchema>;

const CreateProjectHeader: React.FC<{ projectId?: string }> = ({
  projectId,
}) => {
  const { t } = useTranslation();
  return (
    <SecondaryHeader
      title={
        projectId
          ? t('models:projects.edit.title')
          : t('models:projects.create.title')
      }
      searchable={false}
    />
  );
};

const createCreateProjectHeader = (projectId?: string) => () => (
  <CreateProjectHeader projectId={projectId} />
);

const buildFileAttributes = (
  files: FormValues['files']
): DocumentAttributes[] => {
  if (!files || !Array.isArray(files) || files.length === 0) return [];
  return (files as LocalFile[])
    .filter((file) => file.blobId && file.blobId !== '')
    .map((file) => ({
      name: file.name,
      contentType: file.contentType,
      isImage: file.isImage || false,
      isAudio: file.isAudio || false,
      blobId: file.blobId || '',
      clientId: file.clientId,
      duration: file.duration || undefined,
    }));
};

export type ProjectFormStackParamList = {
  'create-project-form': {
    projectId?: Project['id'];
    projectUUId?: Project['uuid'];
  };
  'project-location-search': undefined;
  'member-select': undefined;
  'team-select': { disablePersonalProjects?: boolean };
  'color-select': undefined;
  'icon-select': undefined;
  'project-copy-select': {
    projectId?: string;
    projectUUId?: string;
  };
  'project-copy-search': undefined;
  'create-project-modal': undefined;
};

const Stack = createStackNavigator<ProjectFormStackParamList>();

const ProjectFormStack: React.FC<{
  projectId?: string;
  initialValues: FormValues;
  isShortModal?: boolean;
  externalNavigate?: any;
  // TA-10949 #24: threaded down to ProjectForm — see its own doc comment.
  attemptCloseRef?: React.MutableRefObject<(() => void) | null>;
}> = ({
  projectId,
  initialValues,
  isShortModal,
  externalNavigate,
  attemptCloseRef,
}) => {
  const appNav = useAppNavigation();
  const navigation = externalNavigate || appNav;
  const [teamIdentifier, setTeamUUId] = useState('');
  const [showProjectLimitModal, setShowProjectLimitModal] = useState(false);
  const { t } = useTranslation();
  const {
    setIsCreateProjectOpen,
    setEditProjectUUId,
    setIsTaskWebPanelOpen,
    setIdTaskDetailOpen,
    setIdProjectOfTaskDetailOpen,
    setIsTaskWebPanelMaximize,
    setIdTeamOpen,
    setUUIdTeamOpen,
    setIsTeamPersonal,
    setUpgradeConfirmModalOpen,
    setTeamValues,
    setPaywallModalOpen,
    setPaywallTeam,
    setShowProjectCreatedModal,
    setProjectCreatedName,
  } = useWebDrawer();
  const {
    setShowOverlay,
    setProjectTutorialWebStateAndInStorage,
    projectTutorialWebState,
  } = useProjectTutorialWeb();
  const { data: listProductsData } = useListProductsQuery();
  const { refetch } = useTeamFromQuery({
    teamUUId: teamIdentifier,
  });

  const { startSubscription, restoreSubscription } = useRevenueCat();
  const { teams } = useListTeamsFromQuery({ fetchPolicy: 'cache-first' });
  const [showTeamSubscribed, setShowTeamSubscribed] = useState(false);

  const { trackEvent } = useMixpanel();
  const { markProjectCreated } = useOwnerNudge();
  const { activateNudge: activateInviteMemberNudge } = useInviteMemberNudge();
  const [updateOnboardingState] = useUpdateOnboardingStateMutation();
  // Tracks whether members were added during project creation (checked in onCompleted)
  const submittedHasMembersRef = React.useRef(false);
  // TA-10140: bridges the Formik bag into the useCreateProjectMutation's
  // onError (declared before the <Formik> render prop below) so backend
  // field_errors can be mapped onto the corresponding inputs.
  const formikRef = useRef<FormikProps<FormValues>>(null);

  // Web surfaces a failed submit as an error toast — the same StatusToast the
  // invitation flow below uses — instead of the platform Alert, which on web
  // is a blocking window.alert(). Native keeps Alert.
  const showSubmitError = useCallback(
    (error: unknown) => {
      const message = getSubmitErrorMessage(error, t);
      if (Platform.OS === 'web') {
        showSubmitErrorToast(message);
        return;
      }
      Alert.alert('TaskTag', message);
    },
    [t]
  );

  const showTeamVerificationError = useCallback(
    (
      messageKey:
        | 'models:projects.create.teamInformationLoading'
        | 'models:projects.create.teamUnavailable'
    ) => {
      const message = t(messageKey);
      if (Platform.OS === 'web') {
        showSubmitErrorToast(message);
        return;
      }
      Alert.alert('TaskTag', message);
    },
    [t]
  );

  useEffect(() => {
    if (Platform.OS === 'web') {
      const unsubscribe = navigation
        .getParent()
        ?.addListener('drawerItemPress', (e) => {
          navigation.reset({ routes: [{ name: 'tabs' }], index: 0 });
          e.preventDefault();
        });
      return unsubscribe;
    }
  }, [navigation]);

  const [
    createProject,
    {
      loading: loadingCreateProject,
      data: createProjectData,
      error: createProjectError,
    },
  ] = useCreateProjectMutation({
    refetchQueries: [
      'listProjects',
      'listProjectsRecent',
      'listTeams',
      { query: GetMeDocument },
      'getMe',
    ],
    awaitRefetchQueries: true,
    // TA-10140: map VALIDATION_FAILED field_errors onto the matching Formik
    // field so the user sees an inline error instead of only the generic
    // Alert fallback below (kept for non-field errors, e.g. storage limit).
    onError: (error) => {
      Object.entries(getProjectFormFieldErrors(error)).forEach(
        ([field, message]) =>
          formikRef.current?.setFieldError(field as keyof FormValues, message)
      );
      // Bug fix (code review): a backend field_errors key with no Formik
      // mapping was silently dropped — the form just sat there with no
      // feedback. See shouldShowProjectFormFallbackAlert for why this
      // doesn't double-fire alongside the storageLimitReached alert below.
      // A dropped connection carries no field_errors, so
      // shouldShowProjectFormFallbackAlert is false for it and the failure
      // used to be invisible — hence the explicit isConnectionFailure arm.
      if (
        isConnectionFailure(error) ||
        shouldShowProjectFormFallbackAlert(error)
      ) {
        showSubmitError(error);
      }
    },
    update: (cache, { data }) => {
      if (!data?.createProject) return;

      const isPersonalTeam = (data.createProject.team as { personal?: boolean })
        ?.personal;
      if (!isPersonalTeam) return;

      const meData = cache.readQuery<GetMeQuery>({
        query: GetMeDocument,
      });

      if (!meData?.getMe) return;

      const userId = cache.identify(meData.getMe);

      if (!userId) return;

      cache.modify({
        id: userId,
        fields: {
          personalProjectsCount(existingCount = 0) {
            return existingCount + 1;
          },
        },
      });
    },
  });

  useEffect(() => {
    const project = createProjectData?.createProject;
    if (!project) return;
    trackEvent(MIXPANEL_EVENTS.PROJECT_CREATED, { platform: Platform.OS });
    const isPersonalTeam = !!(project.team as { personal?: boolean })?.personal;
    if (!isPersonalTeam) {
      markProjectCreated();
      if (!submittedHasMembersRef.current) {
        activateInviteMemberNudge(project.uuid);
      }
    }
    const isMobileOnboarding =
      isOnboardingProjectRef.current && Platform.OS !== 'web';
    if (isOnboardingProjectRef.current) {
      setProjectCreatedName(project.name || '');
      setShowProjectCreatedModal(true);
      isOnboardingProjectRef.current = false;
    }
    if (isMobileOnboarding) {
      setIsCreateProjectOpen(false);
      setShowOverlay(false);
      if (dismissBottomSheetFnRef.current) {
        dismissBottomSheetFnRef.current().then(() => {
          if (navigation.canGoBack()) {
            navigation.goBack();
            setTimeout(
              () => navigation.navigateToProject(project, false, true),
              200
            );
          } else {
            navigation.navigateToProject(project, false, true);
          }
        });
      } else {
        if (navigation.canGoBack()) {
          navigation.goBack();
          setTimeout(
            () => navigation.navigateToProject(project, false, true),
            200
          );
        } else {
          navigation.navigateToProject(project, false, true);
        }
      }
      return;
    }
    setTimeout(async () => {
      setIsCreateProjectOpen(false);
      setShowOverlay(false);
      setProjectTutorialWebStateAndInStorage({
        ...projectTutorialWebState,
        PROJECT: 'DONE',
      });
      // Wait for bottom sheet to fully close before navigating
      if (dismissBottomSheetFnRef.current) {
        await dismissBottomSheetFnRef.current();
      }

      // For mobile modal screens, navigate back first to remove the modal from the stack
      // This ensures the BottomSheetModalProvider is unmounted and doesn't block interactions
      if (Platform.OS !== 'web' && isShortModal) {
        // Navigate back from the modal screen to remove it from the stack
        // This will unmount the CreateProject component and its BottomSheetModalProvider
        if (navigation.canGoBack()) {
          navigation.goBack();
          // Wait for the modal screen to be fully removed before navigating to project
          setTimeout(() => {
            navigation.navigateToProject(project);
          }, 200);
        } else {
          // Fallback handling
          navigation.navigateToProject(project);
        }
      } else {
        navigation.navigateToProject(project);
      }

      if (Platform.OS === 'web') {
        setEditProjectUUId('');
      }
    }, 500);
  }, [createProjectData]);

  useEffect(() => {
    if (!createProjectError) return;
    if (
      createProjectError.message ===
      t('models:projects.files.storageLimitReached')
    ) {
      trackEvent(MIXPANEL_EVENTS.FORM_SUBMISSION_FAILED, {
        form_name: 'create_project',
        error_message: createProjectError.message,
      });
      Alert.alert('TaskTag', t('models:projects.files.storageLimitReached'));
    }
  }, [createProjectError]);

  const [updateProject, { data: updateProjectData }] = useUpdateProjectMutation(
    {
      refetchQueries: [
        'listProjects',
        'listProjectsRecent',
        'listActivities',
        'listActivitiesByProject',
      ],
      // PR #5782 review fix: the edit path had no field-error handling at
      // all — VALIDATION_FAILED field_errors on updateProject were
      // completely invisible to the user. Same mapping/fallback as
      // createProject above.
      onError: (error) => {
        Object.entries(getProjectFormFieldErrors(error)).forEach(
          ([field, message]) =>
            formikRef.current?.setFieldError(field as keyof FormValues, message)
        );
        // QA: saving an edited project while offline showed nothing at all —
        // a dropped connection carries no field_errors, so
        // shouldShowProjectFormFallbackAlert is false for it. Same
        // isConnectionFailure arm as createProject above.
        if (
          isConnectionFailure(error) ||
          shouldShowProjectFormFallbackAlert(error)
        ) {
          showSubmitError(error);
        }
      },
    }
  );

  useEffect(() => {
    if (!updateProjectData) return;
    if (Platform.OS === 'web') {
      setIsCreateProjectOpen(false);
      setEditProjectUUId('');
    }
  }, [updateProjectData]);

  const [createProjectInvitation] = useCreateProjectInvitationMutation({
    refetchQueries: ['listProjects', 'getProject'],
    awaitRefetchQueries: true,
  });

  const getProductPlus = listProductsData?.listProducts?.find((product) =>
    product?.name?.toUpperCase()?.includes('PLUS')
  );
  const getProductTeam = listProductsData?.listProducts?.find((product) =>
    product?.name?.toUpperCase()?.includes('TEAM')
  );

  const canCreateProject = async (teamUUId: string) => {
    // This refetch runs before createProject, so an offline failure used to
    // abort the submit silently — the Create button did nothing at all, with
    // only ERR_INTERNET_DISCONNECTED in the console. runSubmitPreflight shows
    // the error and rethrows (see its docs for why rejecting matters). The
    // paywall/limit paths below still return false: those are decisions, not
    // failures.
    const res = await runSubmitPreflight(
      () => refetch({ id: teamUUId || '' }),
      showSubmitError
    );
    const teamUpdated = res?.data?.getTeam;

    if (!teamUpdated) {
      showTeamVerificationError('models:projects.create.teamUnavailable');
      return false;
    }

    const teamPersonal = teamUpdated?.personal;

    const isExpired =
      new Date(teamUpdated?.subscriptionDetails?.expirationDate).getTime() <
      Date.now();

    // Check whether the paywall should be shown for this team on web
    const isWebPaywallRequired =
      Platform.OS === 'web' && shouldShowPaywall(teamUpdated);
    if (isWebPaywallRequired) {
      setPaywallTeam(teamUpdated);
      setPaywallModalOpen(true);
      trackEvent(MIXPANEL_EVENTS.FORM_SUBMISSION_FAILED, {
        form_name: 'create_project',
        error_message: 'Paywall triggered',
      });
      return false;
    }

    // Personal team at project limit — show paywall modal to guide user to upgrade
    if (hasReachedPersonalProjectLimit(teamUpdated)) {
      if (Platform.OS === 'web') {
        setPaywallTeam(teamUpdated);
        setPaywallModalOpen(true);
        trackEvent(MIXPANEL_EVENTS.FORM_SUBMISSION_FAILED, {
          form_name: 'create_project',
          error_message: 'Personal project limit reached',
        });
        setIsCreateProjectOpen(false);
        setEditProjectUUId('');
      } else {
        // Mobile: show the project-limit modal; its CTA leads to upgrade-plus
        setShowProjectLimitModal(true);
        trackEvent(MIXPANEL_EVENTS.FORM_SUBMISSION_FAILED, {
          form_name: 'create_project',
          error_message: 'Personal project limit reached (mobile)',
        });
      }
      return false;
    }

    // Non-personal team: expired subscription with project limit reached
    const displayedLockCreateTeam =
      !teamPersonal && isExpired && !!teamUpdated?.hasReachedMaxProjects;

    if (displayedLockCreateTeam) {
      if (Platform.OS === 'web') {
        Toast.show(
          <CustomToast
            onPress={() => {
              setIsCreateProjectOpen(false);
              setEditProjectUUId('');
              setIsTaskWebPanelOpen(false);
              setIsTaskWebPanelMaximize(false);
              setIdTaskDetailOpen('');
              setIdProjectOfTaskDetailOpen('');
              setIdTeamOpen(teamUpdated?.id);
              setUUIdTeamOpen(teamUpdated?.uuid);
              setIsTeamPersonal(teamPersonal);
              setTeamValues(teamUpdated);
              setUpgradeConfirmModalOpen(true);
            }}
            redirectTo='my-account'
            params={{
              screen: 'create-subscription',
              params: {
                teamUUId: teamUpdated?.uuid,
                isPersonal: teamPersonal,
                isPlanPlus: !!teamPersonal,
                planId: teamPersonal ? getProductPlus?.id : getProductTeam?.id,
              },
            }}
            isTryFree
          />,
          {
            type: 'custom',
            placement: 'bottom',
            duration: 10000,
            animationType: 'zoom-in',
          }
        );
        trackEvent(MIXPANEL_EVENTS.FORM_SUBMISSION_FAILED, {
          form_name: 'create_project',
          error_message: 'Project limit reached or expired subscription',
        });
        setIsCreateProjectOpen(false);
        setEditProjectUUId('');
      } else {
        Toast.show(
          <CustomToast
            onPress={() => {
              navigation.navigate('payments-stack', {
                screen: 'upgrade-plus',
              });
            }}
            redirectTo='payments-stack'
            params={{
              screen: 'upgrade-plus',
            }}
            isTryFree
          />,
          {
            type: 'custom',
            placement: 'bottom',
            duration: 10000,
            animationType: 'zoom-in',
          }
        );
        trackEvent(MIXPANEL_EVENTS.FORM_SUBMISSION_FAILED, {
          form_name: 'create_project',
          error_message:
            'Project limit reached or expired subscription (mobile)',
        });
      }
      return false;
    }
    return true;
  };

  const { me } = useMe();
  const dismissBottomSheetFnRef = useRef<(() => Promise<void>) | null>(null);
  const isOnboardingProjectRef = useRef(false);

  const sendInvitationsForProject = async (
    projectId: string,
    members: ProjectMemberRoleAttributes[]
  ) => {
    try {
      await createProjectInvitation({
        variables: { projectId, projectMembers: members },
      });
      if (Platform.OS === 'web') {
        Toast.show(
          <StatusToast
            message={t('models:projects.members.invitationSent')}
            duration={3000}
            type='success'
          />,
          {
            type: 'custom',
            duration: 3000,
            placement: 'bottom',
            animationType: 'slide-in',
            style: {
              backgroundColor: 'transparent',
              shadowColor: 'transparent',
            },
          }
        );
      }
    } catch {
      if (Platform.OS === 'web') {
        Toast.show(
          <StatusToast
            message={t('models:projects.members.invitationFailed')}
            duration={3000}
            type='error'
          />,
          {
            type: 'custom',
            duration: 3000,
            placement: 'bottom',
            animationType: 'slide-in',
            style: {
              backgroundColor: 'transparent',
              shadowColor: 'transparent',
            },
          }
        );
      }
      // Do not rethrow: project update already succeeded; we only report invitation failure.
    }
  };

  const updateInviteCompleteState = async () => {
    await updateOnboardingState({
      variables: { attributes: { inviteMemberComplete: true } },
      update: (cache) => {
        if (!me?.id) return;
        cache.modify({
          id: cache.identify({ __typename: 'User', id: me.id }),
          fields: {
            onboardingState: (existing) => ({
              ...existing,
              inviteMemberComplete: true,
            }),
          },
        });
      },
    });
  };

  const submit = async (
    values: FormValues,
    setSubmitting: (isSubmitting: boolean) => void
  ) => {
    try {
      setSubmitting(true);
      const {
        teamId,
        teamUUId,
        name,
        location,
        description,
        color,
        icon,
        users,
        files,
        projectMembers,
      } = values;
      const { address, latitude, longitude } = location || {
        address: undefined,
        latitude: undefined,
        longitude: undefined,
      };
      const resolvedTeamUUId = resolveProjectTeamUuid(teamId, teamUUId, teams);
      resolvedTeamUUId && setTeamUUId(resolvedTeamUUId);

      // TA-10800: `users` can carry the same person twice — e.g. selected
      // via two overlapping contact Groups on the web Add Members modal,
      // each copy independently assigned a role (once ADMIN, once VIEWER)
      // — or the same email twice via `projectMembers`'s invite list.
      // buildProjectMemberPayload dedupes both, plus one further wrinkle:
      // it's the whole `members`/`invitedUserMembers`/`allProjectMembers`
      // assembly (not just the underlying dedupe helpers) that has its own
      // direct test coverage, so a future edit here can't silently stop
      // deduping while other tests still pass.
      const { members, invitedUserMembers, allProjectMembers } =
        buildProjectMemberPayload(
          users,
          projectMembers as ProjectMemberRoleAttributes[],
          me?.id
        );

      const projectName = name.trim();

      // Handle file uploads - files are already uploaded when selected
      const fileAttributes = buildFileAttributes(files);

      const attributes = {
        teamId,
        name: projectName,
        address,
        latitude,
        longitude,
        description,
        color,
        icon,
        members,
        files: fileAttributes.length > 0 ? fileAttributes : undefined,
        projectMembers:
          allProjectMembers.length > 0 ? allProjectMembers : undefined,
      };

      if (projectId) {
        const updateResult = await updateProject({
          variables: { id: projectId, attributes },
        });
        // PR #5782 review fix: under errorPolicy: 'all' — and because this
        // mutation supplies onError — this resolves rather than rejecting
        // even when the update never landed, so the invite/onboarding side
        // effects below used to run anyway. Reject instead, so callers can
        // tell a failed edit from a successful one; the message is already on
        // screen (onError above).
        if (mutationFailed(updateResult)) {
          throw toMutationError(updateResult.errors, 'updateProject failed');
        }
        if (invitedUserMembers.length > 0) {
          await sendInvitationsForProject(projectId, invitedUserMembers);
          await updateInviteCompleteState();
        }
      } else {
        if (!resolvedTeamUUId) {
          showTeamVerificationError(
            'models:projects.create.teamInformationLoading'
          );
          return;
        }

        // canCreateProject will check for paywall and show modal if needed
        const res = await canCreateProject(resolvedTeamUUId);
        if (!res) {
          setSubmitting(false);
          return;
        }
        submittedHasMembersRef.current =
          members.length > 0 || allProjectMembers.length > 0;
        isOnboardingProjectRef.current = values.isOnboardingProject ?? false;
        const createResult = await createProject({
          variables: { attributes },
        });

        // PR #5782 review fix: same as above — createProject resolves rather
        // than rejecting (it supplies onError), so a failed create used to
        // fall through to the side effects below.
        if (mutationFailed(createResult)) {
          // The message is already on screen (onError above). Reject so
          // submit callers skip their success-only cleanup: the project
          // bottom sheet's onSuccess resets the form and clears members and
          // attachments, which would throw away the user's input for a
          // project that was never created.
          throw toMutationError(createResult.errors, 'createProject failed');
        }
        if (
          (allProjectMembers.length > 0 || invitedUserMembers.length > 0) &&
          me?.onboardingState?.inviteMemberComplete === false
        ) {
          // TA-10892 #33: record who the Owner brought into the onboarding
          // project (existing users + email invitations) so the web chat
          // list opens that member's chat, or highlights 2+ members' chats.
          if (Platform.OS === 'web') {
            const created = createResult.data?.createProject;
            addOnboardingInvitees([
              ...(created?.members ?? [])
                .map((m) => m.user)
                .filter((u) => !!u?.id && u.id !== me?.id)
                .map((u) => ({ id: u.id, email: u.email ?? undefined })),
              ...(created?.invitations ?? []).map((i) => ({
                email: i.email ?? undefined,
              })),
            ]);
          }
          await updateInviteCompleteState();
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Formik
      innerRef={formikRef}
      initialValues={initialValues}
      onSubmit={(values, { setSubmitting }) => submit(values, setSubmitting)}
      validationSchema={projectFormSchema}>
      {({ values }) => {
        const ownerId = values.users.find(
          (u) => u.role === ProjectMemberRole.Owner
        )?.id;
        const projectLimitModal = showProjectLimitModal && (
          <SubscribeModal
            onRestore={() => {
              if (!me?.id) return;
              // Keep the paywall open until restore resolves — closing early
              // would strand a failed restore with only a fleeting alert and
              // no way back to the CTA (TA-10254 QA finding).
              restoreSubscription(() => {
                setShowProjectLimitModal(false);
                setShowTeamSubscribed(true);
              });
            }}
            onPrimary={async () => {
              const personalTeam = teams.find((t) => t.personal === true);
              if (personalTeam && me?.id) {
                try {
                  // Identify the RC customer as this user before purchasing so
                  // the subscription is attributed to the correct account.
                  await loginRevenueCat(me.id);
                } catch {
                  // Without a successful RC login the purchase would attach to
                  // the wrong customer — abort instead of buying anonymously.
                  return;
                }
                const teamId = personalTeam.id;
                startSubscription(
                  teamId,
                  () => {
                    setShowTeamSubscribed(true);
                  },
                  () => {}
                );
                // Only dismiss once the purchase has been kicked off —
                // startSubscription triggers the native iOS purchase sheet, so
                // closing any earlier would hide the paywall before that sheet
                // ever appears.
                setShowProjectLimitModal(false);
              }
            }}
            onDismiss={() => setShowProjectLimitModal(false)}
          />
        );
        if (isShortModal)
          return (
            <>
              <ProjectCreateBottomSheet
                loadingCreateProject={loadingCreateProject}
                onDismissAfterNavigationReady={(
                  dismissFn: () => Promise<void>
                ) => {
                  dismissBottomSheetFnRef.current = dismissFn;
                }}
              />
              {projectLimitModal}
              {showTeamSubscribed && (
                <SuccessModal
                  title={t('teams.overview.subscription.subscribedTitle')}
                  description={t(
                    'teams.overview.subscription.subscribedDescription'
                  )}
                  primaryLabel={t('teams.overview.subscription.createProject')}
                  onPrimary={() => {
                    setShowTeamSubscribed(false);
                  }}
                  onDismiss={() => setShowTeamSubscribed(false)}
                />
              )}
            </>
          );

        return (
          <>
            <Stack.Navigator
              initialRouteName='create-project-form'
              screenOptions={{
                headerShown: Platform.OS !== 'web',
              }}>
              <Stack.Screen
                name='create-project-form'
                options={() => ({
                  header: createCreateProjectHeader(projectId),
                })}>
                {() => (
                  <ProjectForm
                    screenName={projectId ? 'Edit' : 'Create'}
                    attemptCloseRef={attemptCloseRef}
                  />
                )}
              </Stack.Screen>
              <Stack.Screen
                name='project-location-search'
                component={ProjectLocationSearch}
                options={{
                  headerShown: false,
                }}
              />
              <Stack.Group screenOptions={stackScreenOptions}>
                <Stack.Screen
                  name='member-select'
                  options={{ title: 'Members' }}>
                  {() => (
                    <MemberSelect
                      defaultRole={defaultProjectRole}
                      ownerId={ownerId}
                    />
                  )}
                </Stack.Screen>
                <Stack.Screen
                  name='team-select'
                  component={TeamSelect}
                  options={{ title: 'Team' }}
                />
                <Stack.Screen
                  name='color-select'
                  options={{
                    headerShown: false,
                  }}>
                  {(props) =>
                    Platform.OS === 'web' ? (
                      <WebModal visible onClose={props.navigation.goBack}>
                        <ColorPicker />
                      </WebModal>
                    ) : (
                      <ColorPicker />
                    )
                  }
                </Stack.Screen>
                <Stack.Screen
                  name='icon-select'
                  options={{
                    headerShown: false,
                  }}>
                  {(props) =>
                    Platform.OS === 'web' ? (
                      <WebModal visible onClose={props.navigation.goBack}>
                        <IconPicker />
                      </WebModal>
                    ) : (
                      <MobileIconPicker />
                    )
                  }
                </Stack.Screen>
              </Stack.Group>

              <Stack.Group
                screenOptions={{
                  headerShown: false,
                  presentation: 'transparentModal',

                  cardOverlayEnabled: true,
                  cardStyleInterpolator: ({ current: { progress } }) => ({
                    containerStyle: {
                      opacity: progress.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0, 1],
                        extrapolate: 'clamp',
                      }),
                    },
                    overlayStyle: {
                      opacity: progress.interpolate({
                        inputRange: [0, 1],
                        outputRange: [0, 0.6],
                        extrapolate: 'clamp',
                      }),
                    },
                  }),
                }}>
                <Stack.Screen
                  name='project-copy-search'
                  component={ProjectCopySearch}
                  options={{ title: 'Copy' }}
                />
                <Stack.Screen
                  name='project-copy-select'
                  options={{ title: 'Select' }}>
                  {(props) => (
                    <ProjectCopySelect
                      {...props}
                      onProjectCopySelect={(project: Project) => {
                        navigation.navigateToProject(project);
                      }}
                    />
                  )}
                </Stack.Screen>
              </Stack.Group>
            </Stack.Navigator>
            {projectLimitModal}
          </>
        );
      }}
    </Formik>
  );
};

export { ProjectFormStack };
